/**
 * useContactVerification — consumer hook for ContactVerificationPort.
 *
 * Reads the contactVerification port from PortsContext, resolves the
 * verification state via React.use() (Suspense), and exposes recordVerification
 * as a passthrough callback. Throws a clear error when the port slot is absent.
 *
 * CRITICAL (036): the production `ServerContactVerificationAdapter` is genuinely
 * ASYNC, so this hook suspends on its INITIAL mount. The read promise is cached
 * in a module-level WeakMap keyed by the port instance — NOT a useRef. A
 * component that suspends before it ever commits has its fiber (and any useRef)
 * discarded, so a ref-cached promise is recreated on every retry and the
 * Suspense boundary loops forever (the prompt silently never renders). The
 * WeakMap lives OUTSIDE React: it survives the suspend-retry, resets when the
 * composition swaps the adapter (new key → new instance per React root), and is
 * GC'd with the port. This is exactly the discipline useMemberProfile / useCases
 * / useMyData use for the same reason. The demo fixture is synchronous, so it
 * takes the non-suspending fast path.
 *
 * recordVerification() is intentionally NOT cached: it is a fire-and-forget
 * mutation the prompt invokes on a confirm/update outcome. Passing it straight
 * through keeps that intent explicit.
 *
 * Read-your-writes note: the server adapter ALSO keeps its own "verified now"
 * cache so it satisfies the port contract's RYW clause. In THIS consumer that
 * cache is shadowed — the WeakMap holds the FIRST read promise for the port's
 * lifetime and never re-reads, so after a confirm the prompt relies on its own
 * local `dismissed` flag (ContactVerificationPrompt), not on a re-read; a fresh
 * React root / reload mints a new port instance and re-reads the (now not-due)
 * server truth. This is sufficient because the prompt never remounts on the same
 * port mid-session. If a future consumer needs a live post-write re-read on the
 * same port, add an invalidation store as useMemberProfile does (invalidate +
 * useSyncExternalStore) rather than leaning on the adapter's cache.
 *
 * Only call inside components wrapped in a Suspense boundary; the
 * ContactVerificationGate wraps the prompt in `<Suspense fallback={null}>`.
 *
 * Distinct from the providers/useContactVerificationPort.ts FACTORY hook: that
 * one builds the server adapter for the composition root, this one consumes it.
 *
 * Owned by feature 026-proto-convergence-v2; async-suspense fix by 036.
 * Contract: specs/026-proto-convergence-v2/contracts/contact-verification-port.md
 */

import React, { useContext } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import type {
  ContactVerificationPort,
  ContactVerificationState,
  VerificationOutcome,
} from './ContactVerificationPort.js';

export interface UseContactVerificationResult {
  readonly state: ContactVerificationState;
  readonly recordVerification: (outcome: VerificationOutcome) => void | PromiseLike<void>;
}

function isPromiseLike<T>(value: T | PromiseLike<T>): value is PromiseLike<T> {
  return value !== null && typeof value === 'object' && 'then' in value;
}

interface StateCacheEntry {
  readonly promise: PromiseLike<ContactVerificationState>;
  rejected: boolean;
}

// Module-level read-promise cache keyed by the port INSTANCE — see the header:
// a useRef would be discarded on initial-mount suspense and loop the boundary.
// One read per port instance per app lifetime; a fresh React root / adapter swap
// mints a new port instance (new key) and re-reads (route-on-read).
const statePromiseCache = new WeakMap<ContactVerificationPort, StateCacheEntry>();

export function useContactVerification(): UseContactVerificationResult {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useContactVerification: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.contactVerification === undefined) {
    throw new Error(
      'useContactVerification: contactVerification port is not registered. This feature requires the contact verification port to be configured.',
    );
  }

  const port: ContactVerificationPort = ports.contactVerification;
  const recordVerification = (outcome: VerificationOutcome): void | PromiseLike<void> =>
    port.recordVerification(outcome);

  let entry = statePromiseCache.get(port);
  if (entry === undefined) {
    const stateOrPromise = port.getVerificationState();
    if (!isPromiseLike(stateOrPromise)) {
      // Synchronous adapter (test fixture) — nothing to suspend on.
      return { state: stateOrPromise, recordVerification };
    }
    const newEntry: StateCacheEntry = { promise: stateOrPromise, rejected: false };
    // The server adapter never rejects (it degrades internally), but guard for
    // parity with useMemberProfile: a rejected read is evicted AFTER the boundary
    // commits so retries don't loop, and a later mount re-fetches.
    stateOrPromise.then(undefined, () => {
      newEntry.rejected = true;
    });
    statePromiseCache.set(port, newEntry);
    entry = newEntry;
  } else if (entry.rejected) {
    queueMicrotask(() => statePromiseCache.delete(port));
  }

  return { state: React.use(entry.promise), recordVerification };
}
